import { differenceBy } from 'mojito/utils';

/**
 * Promotion service utilities.
 *
 * @class PromotionsUtils
 * @name utils
 * @memberof Mojito.Services.Promotions
 */
export default class PromotionsUtils {
    /**
     * Merge promotion location objects. If there are promotions with the same id, the ones from the second map will be used.
     * In a map key is location id, and value is a list of promotions for that location.
     *
     * @param {Map<string, Array<Mojito.Services.Promotions.types.Promotion>>} [l1] - First promotion locations map.
     * @param {Map<string, Array<Mojito.Services.Promotions.types.Promotion>>} [l2] - Second promotion locations map.
     *
     * @returns {Map<string, Array<Mojito.Services.Promotions.types.Promotion>>} Merged promotion locations.
     *
     * @function Mojito.Services.Promotions.utils.mergeLocations
     */
    static mergeLocations(l1 = {}, l2 = {}) {
        const mergedPromotions = Object.keys(l2).reduce((acc, locationId) => {
            acc[locationId] = PromotionsUtils.mergePromotions(l1[locationId], l2[locationId]);
            return acc;
        }, {});
        return { ...l1, ...mergedPromotions };
    }

    /**
     * Merge promotion lists. If there are promotions with the same id, the ones from the second list will be used.
     * The merged list will be sorted by displayOrder.
     *
     * @param {Array<Mojito.Services.Promotions.types.Promotion>} [p1] - First promotions list.
     * @param {Array<Mojito.Services.Promotions.types.Promotion>} [p2] - Second promotions list.
     *
     * @returns {Array<Mojito.Services.Promotions.types.Promotion>} List of merged promotions.
     *
     * @function Mojito.Services.Promotions.utils.mergePromotions
     */
    static mergePromotions(p1 = [], p2 = []) {
        const locations = differenceBy(p1, p2, 'id');
        return [...locations, ...p2].sort((a, b) => a.displayOrder - b.displayOrder);
    }
}
